/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Joyride, ACTIONS, EVENTS } from 'react-joyride'
import { withUserStore } from '../../contexts/UserStore'
import { 
  createPrimerStep, 
  navigationSteps, 
  contextSteps,
  sequences
} from './TourSteps'

const TeamMemberTour = props => {
  const [stepIndex, setStepIndex] = useState(0)
  const [isRunning, setIsRunning] = useState(false)
  const [hasShownAutoTour, setHasShownAutoTour] = useState(false)
  
  // Detect account type
  const getAccountType = () => {
    if (!props.userStore?.account) return 'business'
    return props.userStore.account.accountType || 'business'
  }

  // Auto-show tour for new team members
  useEffect(() => {
    if (!props.userStore || !props.userStore.currentAccountsUser || hasShownAutoTour) return
    
    // Skip auto-showing tour if we're in a test environment
    if (window.Cypress || window.location.search.includes('test')) return
    
    const accountsUser = props.userStore.currentAccountsUser
    const goals = props.userStore.getRecords('goals') || []
    
    // Check if user is a team member (not team lead)
    const isTeamMember = accountsUser.teamRole === 'team_member'
    
    // Check if user has seen the tour before (using settings)
    const hasSeenTour = props.userStore.getAccountsUserSetting('hasSeenTeamMemberTour')
    
    // Show tour if:
    // 1. User is a team member
    // 2. Has not seen the tour before
    // 3. Has no goals yet (new to the account)
    if (isTeamMember && !hasSeenTour && goals.length === 0) {
      props.tourActions.setShowTour('TeamMemberTour')
      setHasShownAutoTour(true)
      // Mark tour as seen
      props.userStore.setAccountsUserSetting('hasSeenTeamMemberTour', true)
    }
  }, [props.userStore?.currentAccountsUser, hasShownAutoTour])

  // Start tour when TeamMemberTour is activated
  useEffect(() => {
    if (props.tourActions.showTour() === 'TeamMemberTour') {
      setIsRunning(true)
      setStepIndex(0)
    } else {
      setIsRunning(false)
      // Remove blocking overlay when tour ends
      const style = document.getElementById('tour-blocking-overlay')
      if (style) {
        style.remove()
      }
    }
  }, [props.tourActions.showTour()])

  // Update CSS to only allow clicks on current step's target
  useEffect(() => {
    if (!isRunning) return
    
    // Never apply blocking overlay during tests unless specifically enabled
    if (window.Cypress && !window.cypressAllowTourOverlay) return

    // Remove existing style
    const existingStyle = document.getElementById('tour-blocking-overlay')
    if (existingStyle) {
      existingStyle.remove()
    }

    // Get the target selector for current step
    let currentTarget = ''
    if (stepIndex < steps.length) {
      const step = steps[stepIndex]
      if (step.target !== 'body') {
        currentTarget = step.target
      }
    }

    // Create new style with current target
    const style = document.createElement('style')
    style.id = 'tour-blocking-overlay'
    style.textContent = `
      body * {
        pointer-events: none !important;
      }
      [data-floating-ui-portal] *,
      .react-joyride__tooltip *,
      .react-joyride__spotlight,
      .react-joyride__overlay,
      .react-joyride *,
      .modal *,
      .modal-body input,
      .modal-footer button${currentTarget ? `,\n      ${currentTarget}` : ''} {
        pointer-events: auto !important;
      }
    `
    document.head.appendChild(style)
  }, [isRunning, stepIndex])

  // Helper function to advance to next step
  const advanceStep = () => {
    setStepIndex(prev => prev + 1)
  }

  const steps = [
    // Welcome step
    {
      target: 'body',
      content: (
        <div>
          <h4>Welcome to the Team! 👋</h4>
          <p>
            Great to have you on board! As a team member, you'll contribute to achieving 
            our priorities through team strategies and your personal goals.
          </p>
          <p>Let's walk through how everything works and help you set up your first goals.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'center',
      offset: 5
    },
    
    // Framework primer
    createPrimerStep(getAccountType()),
    
    // View priorities and strategies sequence
    ...sequences.viewPrioritiesAndStrategies(),
    
    // Personal goal creation
    navigationSteps.toGoals(
      <div>
        <h5>Step 3: Set Your Personal Goals</h5>
        <p>
          Now it's time to set your personal goals that support the team strategies. 
          Click "Goals" to continue.
        </p>
      </div>
    ),
    ...sequences.createGoal('team_member'),
    
    contextSteps.goalSuccess(
      <div>
        <h4>Excellent Start! 🌟</h4>
        <p>You've created your first goal! Consider adding 2-3 more goals that:</p>
        <ul>
          <li>Support different team strategies</li>
          <li>Help you grow professionally</li>
          <li>Can be achieved this quarter</li>
        </ul>
        <p>Let's look at one more important area.</p>
      </div>
    ),
    
    // Dashboard introduction
    navigationSteps.toHome(
      <div>
        <h5>Your Dashboard</h5>
        <p>
          Click here anytime to return to your dashboard. It shows your progress 
          on goals and important team updates.
        </p>
      </div>
    ),
    
    // Final completion step
    {
      target: 'body',
      content: (
        <div>
          <h4>You're Ready to Go! 🚀</h4>
          <p>As a team member, you now understand:</p>
          <ul>
            <li>✅ Organizational priorities that guide decisions</li>
            <li>✅ Team strategies you'll follow</li>
            <li>✅ How to set personal goals that contribute</li>
          </ul>
          <p><strong>Tips for Success:</strong></p>
          <ul>
            <li>📅 Review your goals weekly</li>
            <li>💬 Ask your team lead if strategies are unclear</li>
            <li>📈 Update goal progress regularly</li>
            <li>🤝 Help teammates achieve their goals too</li>
          </ul>
          <div className="mt-3">
            <button 
              className="btn btn-primary me-2"
              onClick={() => {
                props.tourActions.setShowTour(false)
              }}
            >
              Start Contributing! 💪
            </button>
            <button 
              className="btn btn-outline-secondary"
              onClick={() => {
                document.querySelector('#goals-link')?.click()
                props.tourActions.setShowTour(false)
              }}
            >
              Add More Goals 🎯
            </button>
          </div>
        </div>
      ),
      disableBeacon: true,
      placement: 'center',
      offset: 5
    }
  ]

  const joyrideStateChanged = (s) => {
    const { action, index, status, type } = s

    if (status === 'finished' || status === 'skipped' || action === ACTIONS.CLOSE) {
      props.tourActions.setShowTour(false)
      setIsRunning(false)
      setStepIndex(0)
      // Clean up CSS blocking overlay
      const style = document.getElementById('tour-blocking-overlay')
      if (style) {
        style.remove()
      }
    } else if (action === ACTIONS.NEXT && type === EVENTS.STEP_AFTER) {
      setStepIndex(index + 1)
    } else if (action === ACTIONS.PREV && type === EVENTS.STEP_AFTER) {
      setStepIndex(index - 1)
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const style = document.getElementById('tour-blocking-overlay')
      if (style) {
        style.remove()
      }
    }
  }, [])

  // Set up click handlers for target elements to advance tour
  useEffect(() => {
    if (!isRunning) return

    const handleMenuClick = () => {
      if (stepIndex === 2 || stepIndex === 5) {
        setTimeout(() => advanceStep(), 100)
      }
    }

    const handlePrioritiesClick = () => {
      if (stepIndex === 3) {
        setTimeout(() => advanceStep(), 100)
      }
    }

    const handleStrategiesClick = () => {
      if (stepIndex === 6) {
        setTimeout(() => advanceStep(), 100)
      }
    }

    const handleGoalsClick = () => {
      if (stepIndex === 8) {
        setTimeout(() => advanceStep(), 100)
      }
    }

    const handleAddButtonClick = () => {
      if (stepIndex === 9) {
        setTimeout(() => advanceStep(), 1000)
      }
    }

    const handleSaveButtonClick = () => {
      if (stepIndex === 12) {
        setTimeout(() => advanceStep(), 500)
      }
    }

    const handleHomeClick = () => {
      if (stepIndex === 15) {
        setTimeout(() => advanceStep(), 100)
      }
    }

    // Add event listeners
    const menuLink = document.querySelector('#menu-link')
    const prioritiesLink = document.querySelector('[data-cy="nav-priorities"]')
    const strategiesLink = document.querySelector('[data-cy="nav-strategies"]')
    const goalsLink = document.querySelector('#goals-link')
    const addButton = document.querySelector('[data-cy="add-button"]')
    const saveButton = document.querySelector('[data-cy="modal-save-button"]')
    const homeLink = document.querySelector('#home-link')

    if (menuLink) menuLink.addEventListener('click', handleMenuClick)
    if (prioritiesLink) prioritiesLink.addEventListener('click', handlePrioritiesClick)
    if (strategiesLink) strategiesLink.addEventListener('click', handleStrategiesClick)
    if (goalsLink) goalsLink.addEventListener('click', handleGoalsClick)
    if (addButton) addButton.addEventListener('click', handleAddButtonClick)
    if (saveButton) saveButton.addEventListener('click', handleSaveButtonClick)
    if (homeLink) homeLink.addEventListener('click', handleHomeClick)

    // Cleanup
    return () => {
      if (menuLink) menuLink.removeEventListener('click', handleMenuClick)
      if (prioritiesLink) prioritiesLink.removeEventListener('click', handlePrioritiesClick)
      if (strategiesLink) strategiesLink.removeEventListener('click', handleStrategiesClick)
      if (goalsLink) goalsLink.removeEventListener('click', handleGoalsClick)
      if (addButton) addButton.removeEventListener('click', handleAddButtonClick)
      if (saveButton) saveButton.removeEventListener('click', handleSaveButtonClick)
      if (homeLink) homeLink.removeEventListener('click', handleHomeClick)
    }
  }, [isRunning, stepIndex])

  return (
    <Joyride
      stepIndex={stepIndex}
      steps={steps} 
      spotlightPadding={5} 
      spotlightClicks={true}
      disableOverlayClose={true}
      disableOverlay={true}
      callback={joyrideStateChanged}
      run={isRunning}
      continuous={true}
      showProgress={true}
      showSkipButton={true}
      styles={{
        options: {
          primaryColor: '#17a2b8',
          width: 450,
          zIndex: 10002,
        },
        spotlight: {
          backgroundColor: 'rgba(0, 0, 0, 0)',
          border: '2px solid #17a2b8',
        },
        tooltip: {
          fontSize: '14px',
          padding: '20px',
          zIndex: 10003,
        }
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour'
      }}
    />
  )
}

TeamMemberTour.propTypes = {
  tourActions: PropTypes.object.isRequired,
  userStore: PropTypes.object
}

export default withUserStore(TeamMemberTour)